import { Fragment, useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useApi } from "../../hooks/useApi";
import { useCompanyId } from "../../hooks/useCompanyId";
import CoverageBar from "./CoverageBar";

interface AccountPaymentCoverage {
  checkoutChampAccountId: number;
  name: string;
  paymentCacheEnabled: boolean;
  timezone: string;
  expectedDays: number;
  successDays: number;
  failedDays: number;
  inProgressDays: number;
  missingDays: number;
  customerInfoEnabled: boolean;
  customerInfoEligibleDays: number;
  customerInfoSuccessDays: number;
  customerInfoFailedDays: number;
  customerInfoInProgressDays: number;
  customerInfoMissingDays: number;
  oldestSuccess: string | null;
  newestSuccess: string | null;
}

interface CoverageResponse {
  accounts: AccountPaymentCoverage[];
}

interface BackfillResponse {
  checkoutChampAccountId: number;
  requestedDays: number;
  daysQueued: number;
  daysSkippedAlreadySuccess: number;
}

type DateStatus = "success" | "failed" | "in_progress" | "missing";

interface DateStatusEntry {
  date: string;
  status: DateStatus;
  insertedCount: number;
  attempts: number;
  lastError: string | null;
  lastSyncedAt: string | null;
  customerInfoStatus: DateStatus | null;
  customerInfoInsertedCount: number | null;
  customerInfoAttempts: number | null;
  customerInfoLastError: string | null;
  customerInfoLastSyncedAt: string | null;
}

interface DateStatusResponse {
  dates: DateStatusEntry[];
}

const STATUS_LABEL: Record<DateStatus, string> = {
  success: "Success",
  failed: "Failed",
  in_progress: "In progress",
  missing: "Missing",
};

const STATUS_COLOR: Record<DateStatus, "success" | "error" | "info" | "warning"> = {
  success: "success",
  failed: "error",
  in_progress: "info",
  missing: "warning",
};

export default function CheckoutChampPaymentsCoverage() {
  const { get, post, loading } = useApi();
  const { companyId } = useCompanyId();
  const [accounts, setAccounts] = useState<AccountPaymentCoverage[] | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAccount, setMenuAccount] = useState<AccountPaymentCoverage | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [datesDialogAccount, setDatesDialogAccount] = useState<AccountPaymentCoverage | null>(null);
  const [dateEntries, setDateEntries] = useState<DateStatusEntry[] | null>(null);
  const [datesLoading, setDatesLoading] = useState(false);
  const [reimportingDate, setReimportingDate] = useState<string | null>(null);

  const fetchCoverage = useCallback(async () => {
    try {
      const query = companyId ? `?companyId=${companyId}` : "";
      const result = await get<CoverageResponse>(`/api/admin/payments-cache/checkout-champ/coverage${query}`);
      setAccounts(result.accounts);
    } catch (err) {
      console.error("Failed to load CheckoutChamp payments coverage", err);
      setAccounts([]);
    }
  }, [get, companyId]);

  useEffect(() => {
    fetchCoverage();
  }, [fetchCoverage]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, account: AccountPaymentCoverage) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuAccount(account);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuAccount(null);
  };

  const handleBackfill = async () => {
    const account = menuAccount;
    handleCloseMenu();
    if (!account) return;

    try {
      const result = await post<BackfillResponse>(
        `/api/admin/payments-cache/checkout-champ/${account.checkoutChampAccountId}/backfill`,
        {},
      );
      setSnackbar({
        open: true,
        message: `Queued ${result.daysQueued} day(s) for ${account.name}; ${result.daysSkippedAlreadySuccess} already succeeded`,
        severity: "success",
      });
      fetchCoverage();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message ?? "Failed to queue backfill",
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenDates = async (account: AccountPaymentCoverage) => {
    setDatesDialogAccount(account);
    setDateEntries(null);
    setDatesLoading(true);
    try {
      const result = await get<DateStatusResponse>(
        `/api/admin/payments-cache/checkout-champ/${account.checkoutChampAccountId}/dates`,
      );
      setDateEntries(result.dates);
    } catch (err: any) {
      setDateEntries([]);
      setSnackbar({
        open: true,
        message: err?.message ?? "Failed to load date statuses",
        severity: "error",
      });
    } finally {
      setDatesLoading(false);
    }
  };

  const handleCloseDatesDialog = () => {
    setDatesDialogAccount(null);
    setDateEntries(null);
  };

  const handleReimportDate = async (date: string) => {
    const account = datesDialogAccount;
    if (!account) return;
    setReimportingDate(date);
    try {
      const result = await post<{ enqueued: boolean }>(
        `/api/admin/payments-cache/checkout-champ/${account.checkoutChampAccountId}/dates/${date}/reimport`,
        {},
      );
      setSnackbar({
        open: true,
        message: result.enqueued
          ? `Re-import queued for ${date}`
          : `Skipped — a job for ${date} is already in progress`,
        severity: "success",
      });
      const refreshed = await get<DateStatusResponse>(
        `/api/admin/payments-cache/checkout-champ/${account.checkoutChampAccountId}/dates`,
      );
      setDateEntries(refreshed.dates);
      fetchCoverage();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message ?? "Failed to queue re-import",
        severity: "error",
      });
    } finally {
      setReimportingDate(null);
    }
  };

  if (loading && !accounts) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!accounts || accounts.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {companyId
            ? "No CheckoutChamp accounts with payment ingestion enabled for this company."
            : "No CheckoutChamp accounts with payment ingestion enabled."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        CheckoutChamp transactions
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Account</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Coverage</TableCell>
              <TableCell align="right">Success</TableCell>
              <TableCell align="right">Failed</TableCell>
              <TableCell align="right">In progress</TableCell>
              <TableCell align="right">Missing</TableCell>
              <TableCell>Range</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.map((row) => {
              const showPiiRow = row.customerInfoEnabled;
              const rowSpan = showPiiRow ? 2 : 1;
              const subRowSx = showPiiRow ? { borderBottom: "none" } : {};
              const failedChip = (n: number) =>
                n > 0 ? <Chip size="small" label={n} color="error" /> : 0;
              return (
                <Fragment key={row.checkoutChampAccountId}>
                  <TableRow>
                    <TableCell rowSpan={rowSpan}>
                      <Box>
                        <Typography variant="body2">{row.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.timezone}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={subRowSx}>
                      <Chip size="small" variant="outlined" label="Payments" />
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 160, ...subRowSx }}>
                      <CoverageBar
                        eligible={row.expectedDays}
                        success={row.successDays}
                        inProgress={row.inProgressDays}
                        failed={row.failedDays}
                        missing={row.missingDays}
                        onClick={() => handleOpenDates(row)}
                      />
                    </TableCell>
                    <TableCell align="right" sx={subRowSx}>
                      {row.successDays} / {row.expectedDays}
                    </TableCell>
                    <TableCell align="right" sx={subRowSx}>{failedChip(row.failedDays)}</TableCell>
                    <TableCell align="right" sx={subRowSx}>{row.inProgressDays}</TableCell>
                    <TableCell align="right" sx={subRowSx}>{row.missingDays}</TableCell>
                    <TableCell rowSpan={rowSpan}>
                      <Typography variant="caption" color="text.secondary">
                        {row.oldestSuccess ?? "—"} → {row.newestSuccess ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" rowSpan={rowSpan}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenMenu(e, row)}
                        aria-label="Account actions"
                        aria-haspopup="true"
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {showPiiRow && (
                    <TableRow>
                      <TableCell>
                        <Chip size="small" variant="outlined" color="info" label="PII" />
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: 160 }}>
                        <CoverageBar
                          eligible={row.customerInfoEligibleDays}
                          success={row.customerInfoSuccessDays}
                          inProgress={row.customerInfoInProgressDays}
                          failed={row.customerInfoFailedDays}
                          missing={row.customerInfoMissingDays}
                          onClick={() => handleOpenDates(row)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {row.customerInfoEligibleDays > 0
                          ? `${row.customerInfoSuccessDays} / ${row.customerInfoEligibleDays}`
                          : "—"}
                      </TableCell>
                      <TableCell align="right">{failedChip(row.customerInfoFailedDays)}</TableCell>
                      <TableCell align="right">{row.customerInfoInProgressDays}</TableCell>
                      <TableCell align="right">{row.customerInfoMissingDays}</TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleBackfill}>
          <ListItemText>Backfill missing days</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(datesDialogAccount)}
        onClose={handleCloseDatesDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Per-date status
          {datesDialogAccount && (
            <Typography variant="caption" component="div" color="text.secondary">
              {datesDialogAccount.name} — {datesDialogAccount.timezone}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {datesLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!datesLoading && dateEntries && dateEntries.length === 0 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No dates in window.
              </Typography>
            </Box>
          )}
          {!datesLoading && dateEntries && dateEntries.length > 0 && (
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 120 }}>Date</TableCell>
                    <TableCell sx={{ width: 140 }}>Status</TableCell>
                    <TableCell sx={{ width: 160 }}>Contents</TableCell>
                    <TableCell align="right" sx={{ width: 100 }}>Inserted</TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>Attempts</TableCell>
                    <TableCell>Detail</TableCell>
                    <TableCell align="right" sx={{ width: 80 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dateEntries.map((entry) => (
                    <TableRow key={entry.date}>
                      <TableCell sx={{ fontFamily: "monospace" }}>{entry.date}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={STATUS_LABEL[entry.status]}
                          color={STATUS_COLOR[entry.status]}
                        />
                      </TableCell>
                      <TableCell>
                        {entry.status === "success" ? (
                          <Chip
                            size="small"
                            color={entry.customerInfoStatus === "success" ? "success" : "default"}
                            label={entry.customerInfoStatus === "success" ? "Payments + PII" : "Payments only"}
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {entry.status === "success" ? entry.insertedCount : "—"}
                      </TableCell>
                      <TableCell align="right">
                        {entry.attempts > 0 ? entry.attempts : "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {entry.status === "failed" && entry.lastError ? (
                          <Tooltip title={entry.lastError}>
                            <Typography variant="caption" color="error" sx={{
                              display: "block",
                              maxWidth: 360,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}>
                              {entry.lastError}
                            </Typography>
                          </Tooltip>
                        ) : entry.status === "success" && entry.lastSyncedAt ? (
                          <Typography variant="caption" color="text.secondary">
                            synced {new Date(entry.lastSyncedAt).toLocaleString()}
                          </Typography>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Re-import this date">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleReimportDate(entry.date)}
                              disabled={reimportingDate === entry.date}
                              aria-label={`Re-import ${entry.date}`}
                            >
                              {reimportingDate === entry.date
                                ? <CircularProgress size={16} />
                                : <RefreshIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDatesDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
